import { filterSupportedManuscriptFiles, isWordTempFile } from "@/lib/utils/manuscriptFileImport";
import { fileNameToTitle } from "@/lib/utils/fileName";

/** 一度にインポートできるファイル数の上限。大量ファイルによるタブフリーズ/OOMを防ぐ。 */
export const MAX_IMPORT_FILE_COUNT = 200;

export type ImportSelectionErrorKey = "noSupportedFile" | "unsupportedFileIncluded" | "tooManyFiles";

export type ImportSelectionResult =
  | { status: "ok"; files: File[]; folderName: string | null }
  | { status: "error"; errorKey: ImportSelectionErrorKey };

/**
 * 作品インポートダイアログの選択判定（フォルダ/ファイル選択・ドロップ共通）を行う純粋関数。
 *
 * - `folderName` が渡されていれば「フォルダ経由の選択」とみなす（フォルダ名は呼び出し側で
 *   導出済みのものをそのまま受け取る）。
 * - ファイル経由（`folderName` 省略）で対応形式外のファイルが1件でも混ざっていれば選択全体を
 *   拒否する。Word の一時ファイル（`~$xxx.docx`）は本人が意図して選んだものではないため、
 *   この判定の母数から除外する（常に黙って除外する対象）。
 * - フォルダ経由はサムネイル等の無関係なファイルが混在するのが通常のため、この判定の対象外
 *   （黙って除外する）。
 * - 対応形式のファイルが1件も無い場合、または上限件数を超える場合はエラーとする。
 * - フォルダ名は保持前に NFC 正規化する（macOS の NFD 対策。表示・作品名の両方に効く）。
 * - 対応形式のファイルはファイル名の自然順昇順（数字は数値として比較、それ以外は日本語辞書順、
 *   同順位は文字コード順）に並び替えられる（{@link filterSupportedManuscriptFiles} が行う。
 *   選択/ドロップ順はブラウザ・OS依存で不定なため、原稿の作成順・並び順が偏るのを防ぐ）。
 */
export function judgeImportSelection(files: File[], folderName?: string): ImportSelectionResult {
  const supported = filterSupportedManuscriptFiles(files);
  const isFolder = folderName !== undefined;

  if (supported.length === 0) {
    return { status: "error", errorKey: "noSupportedFile" };
  }

  const nonTempCount = files.filter((file) => !isWordTempFile(file)).length;
  if (!isFolder && supported.length < nonTempCount) {
    return { status: "error", errorKey: "unsupportedFileIncluded" };
  }

  if (supported.length > MAX_IMPORT_FILE_COUNT) {
    return { status: "error", errorKey: "tooManyFiles" };
  }

  return {
    status: "ok",
    files: supported,
    folderName: isFolder ? folderName.normalize("NFC") : null,
  };
}

/**
 * `<input webkitdirectory>` で選択された先頭ファイルの `webkitRelativePath` からフォルダ名を導出する。
 *
 * `webkitRelativePath` はサブフォルダ内のファイルも `root/sub/a.txt` のように返すため、
 * 先頭セグメント（ルートフォルダ名）のみを採用する。取得できない（空文字・未対応ブラウザ）場合は
 * 空の作品名を作らないよう、先頭ファイル名（拡張子除去・NFC正規化）にフォールバックする。
 */
export function deriveFolderName(webkitRelativePath: string | undefined, fallbackFileName: string): string {
  const [rootDir] = (webkitRelativePath ?? "").split("/");
  return rootDir || fileNameToTitle(fallbackFileName);
}
